/**
 * Where the assistant may send the owner.
 *
 * The assistant writes Markdown and reads text written by the salon's own
 * customers, so every href in its answer is model output an attacker may have
 * influenced. The only links that stay links are the app's own routes
 * (relative paths): those navigate inside the app. Everything else — http(s),
 * `mailto:`, `tel:`, `javascript:`, `data:`, `//host`, `/\host`, a path the app
 * does not have — is shown as plain text, never as something to click: a link
 * can lure the owner off-site, and its URL can carry data out.
 */

export type HrefKind = 'internal' | 'plain';

/** The support-ticket form: where every path out of the assistant ends. */
export const TICKET_FORM_PATH = '/help?tab=tickets&new=1';
export const INTELLIGENCE_PATH = '/intelligence';

/**
 * Routes of the app the assistant can link to (App.tsx). A path below one of
 * them (`/conversations/42`) counts too. An invented path ("/einstellungen")
 * would land on an empty page, so it is not offered as a link at all.
 */
const LINKABLE_ROUTES: readonly string[] = [
  '/dashboard',
  '/intelligence',
  '/profile',
  '/voice-agent',
  '/conversations',
  '/customers',
  '/reminders',
  '/privacy',
  '/help',
  '/onboarding',
];

/** Whitespace, control characters and backslashes: never part of a link we follow. */
function hasForbiddenChar(value: string): boolean {
  for (let i = 0; i < value.length; i += 1) {
    const code = value.charCodeAt(i);
    if (code <= 0x20 || code === 0x7f || code === 0x5c) return true;
  }
  return false;
}

const ORIGIN_PROBE = 'https://app.invalid';

function isLinkableRoute(pathname: string): boolean {
  const path = pathname.toLowerCase();
  return LINKABLE_ROUTES.some((route) => path === route || path.startsWith(`${route}/`));
}

function isInternalHref(href: string): boolean {
  // Exactly one leading slash: "//host" is another site.
  if (!href.startsWith('/') || href.startsWith('//') || hasForbiddenChar(href)) return false;
  const rawPath = href.split(/[?#]/, 1)[0];
  try {
    // A path the URL parser would rewrite ("/profile/../x") is not what it says.
    return new URL(href, ORIGIN_PROBE).pathname === rawPath && isLinkableRoute(rawPath);
  } catch {
    return false;
  }
}

export function classifyHref(href: string | null | undefined): HrefKind {
  return typeof href === 'string' && isInternalHref(href) ? 'internal' : 'plain';
}

/** The ticket form, optionally with the conversation attached. */
export function ticketFormPath(conversationId?: number | null): string {
  return conversationId ? `${TICKET_FORM_PATH}&c=${conversationId}` : TICKET_FORM_PATH;
}

/** `/intelligence?c=<id>` ("Problem melden" from a conversation) or `?report=1`. */
export function reportProblemPath(conversationId?: number | null): string {
  return conversationId ? `${INTELLIGENCE_PATH}?c=${conversationId}` : `${INTELLIGENCE_PATH}?report=1`;
}
